import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { QboSyncLogEntry } from '@catalyst-psa/shared';
import { QBO_SYNC_MODE_LABELS } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { formatDateTime } from '../../utils/date.js';
import { useQboSyncLog } from './api.js';

const STATUS_TONES = {
  synced: 'green',
  dry_run: 'amber',
  failed: 'red',
  pending: 'gray',
} as const;

const STATUS_LABELS = {
  synced: 'Sent to QBO',
  dry_run: 'Dry run',
  failed: 'Failed',
  pending: 'Pending',
} as const;

interface QboSyncLogPanelProps {
  /** When set, shows only this invoice's log (and hides the invoice/customer columns). */
  invoiceId?: string;
  /** Card title. */
  title?: string;
  enabled?: boolean;
}

/**
 * Detailed QBO sync log — one row per invoice per single/batch run. Used on the
 * qbo-batch page (all recent runs) and the invoice detail page (that invoice's history).
 */
export function QboSyncLogPanel({
  invoiceId,
  title = 'Sync log',
  enabled = true,
}: QboSyncLogPanelProps): JSX.Element {
  const query = useQboSyncLog(invoiceId, enabled);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const rows = query.data ?? [];
  const perInvoice = Boolean(invoiceId);

  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const colSpan = perInvoice ? 6 : 8;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        <span className="text-xs text-slate-500">{rows.length} entries</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">When</th>
              {perInvoice ? null : <th className="px-3 py-2 font-medium">Invoice</th>}
              {perInvoice ? null : <th className="px-3 py-2 font-medium">Customer</th>}
              <th className="px-3 py-2 font-medium">Run</th>
              <th className="px-3 py-2 font-medium">Result</th>
              <th className="px-3 py-2 font-medium">QBO</th>
              <th className="px-3 py-2 font-medium">Email</th>
              <th className="px-3 py-2 font-medium">By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              isEmpty={rows.length === 0}
              emptyTitle="No sync activity yet."
              colSpan={colSpan}
              onRetry={() => void query.refetch()}
            >
              {rows.map((entry) => (
                <QboSyncLogRow
                  key={entry.id}
                  entry={entry}
                  perInvoice={perInvoice}
                  open={expanded.has(entry.id)}
                  onToggle={() => toggle(entry.id)}
                  colSpan={colSpan}
                />
              ))}
            </DataState>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function QboSyncLogRow({
  entry,
  perInvoice,
  open,
  onToggle,
  colSpan,
}: {
  entry: QboSyncLogEntry;
  perInvoice: boolean;
  open: boolean;
  onToggle: () => void;
  colSpan: number;
}): JSX.Element {
  const hasDetail = Boolean(entry.error) || entry.emailRecipients.length > 0 || Boolean(entry.emailNote);
  return (
    <>
      <tr
        className={hasDetail ? 'cursor-pointer hover:bg-slate-50' : ''}
        onClick={hasDetail ? onToggle : undefined}
      >
        <td className="whitespace-nowrap px-3 py-2 text-slate-600">
          {formatDateTime(entry.createdAt)}
        </td>
        {perInvoice ? null : (
          <td className="px-3 py-2 font-medium">
            {/* stopPropagation so opening the invoice doesn't toggle the detail row */}
            <Link
              to={`/invoices/${entry.invoiceId}`}
              onClick={(e) => e.stopPropagation()}
              className="text-brand-600 hover:underline"
            >
              {entry.invoiceNumber}
            </Link>
          </td>
        )}
        {perInvoice ? null : (
          <td className="px-3 py-2">
            {entry.customerName ? (
              <Link
                to={`/invoices/${entry.invoiceId}`}
                onClick={(e) => e.stopPropagation()}
                className="text-brand-600 hover:underline"
              >
                {entry.customerName}
              </Link>
            ) : (
              '—'
            )}
          </td>
        )}
        <td className="px-3 py-2 text-slate-600">{QBO_SYNC_MODE_LABELS[entry.mode]}</td>
        <td className="px-3 py-2">
          <Badge tone={STATUS_TONES[entry.status]}>{STATUS_LABELS[entry.status]}</Badge>
        </td>
        <td className="px-3 py-2 text-slate-600">
          {entry.qboInvoiceUrl ? (
            <a
              href={entry.qboInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="font-medium text-brand-600 hover:underline"
            >
              Open ↗
            </a>
          ) : (
            '—'
          )}
        </td>
        <td className="px-3 py-2 text-slate-600">
          {entry.emailed ? (
            <span className="text-emerald-600">Sent</span>
          ) : entry.emailNote ? (
            <span className="text-amber-600">Skipped</span>
          ) : (
            '—'
          )}
        </td>
        <td className="px-3 py-2 text-slate-500">{entry.actorName ?? '—'}</td>
      </tr>
      {open && hasDetail ? (
        <tr className="bg-slate-50/60">
          <td colSpan={colSpan} className="px-3 py-2">
            {entry.error ? (
              <p className="text-sm text-rose-700">
                <span className="font-medium">Error:</span> {entry.error}
              </p>
            ) : null}
            {entry.emailNote ? (
              <p className="text-sm text-amber-700">
                <span className="font-medium">Email:</span> {entry.emailNote}
              </p>
            ) : null}
            {entry.emailRecipients.length > 0 ? (
              <p className="text-sm text-slate-600">
                <span className="font-medium">Emailed to:</span> {entry.emailRecipients.join(', ')}
              </p>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}
